import { ToastMessage } from "@/components/ToastMessage";
import {
  FaucetActivity,
  FaucetStatusResponse,
  MultipleTokensRequest,
  MultipleTokensResponse,
  TokensEligibilityRequest,
  TokensEligibilityResponse,
  TokensResponse,
} from "@/services/types/faucet";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Decimal from "decimal.js";

// Faucet API base URL - should be set in your environment variables
const FAUCET_API_URL =
  process.env.NEXT_PUBLIC_API_URL || "https://faucet-api.testnet.riselabs.xyz";

export function useFaucetStatus() {
  return useQuery<FaucetStatusResponse, Error>({
    queryKey: ["status"],
    queryFn: async () => {
      const url = `${FAUCET_API_URL}/faucet/status`;
      const response = await fetch(url);

      return await response.json();
    },
  });
}

export function useSupportedTokens() {
  return useQuery<TokensResponse, Error>({
    queryKey: ["tokens"],
    queryFn: async () => {
      const url = `${FAUCET_API_URL}/faucet/tokens`;
      const response = await fetch(url);

      return await response.json();
    },
  });
}

export function useTokensEligibility(props: TokensEligibilityRequest) {
  const { address, tokens } = props;

  return useQuery<TokensEligibilityResponse, Error>({
    enabled: !!address && !!tokens,
    queryKey: [address, tokens],
    queryFn: async () => {
      const url = `${FAUCET_API_URL}/faucet/multi-eligibility?address=${address}&tokens=${tokens}`;
      const response = await fetch(url);

      return await response.json();
    },
  });
}

export function useFaucetActivity() {
  return useQuery<FaucetActivity, Error>({
    queryKey: ["activity"],
    queryFn: async () => {
      const url = `${FAUCET_API_URL}/faucet/activity`;
      const response = await fetch(url);

      return await response.json();
    },
    refetchInterval: 2000,
  });
}

export function useRequestTokens() {
  return useMutation<MultipleTokensResponse, Error, MultipleTokensRequest>({
    mutationFn: async (request: MultipleTokensRequest) => {
      const url = `${FAUCET_API_URL}/faucet/multi-request`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      });

      return await response.json();
    },
    onMutate() {
      toast(
        <ToastMessage
          title="Please wait..."
          content="Request has been submitted!"
        />,
        { type: "info" }
      );
    },
    onSuccess(data) {
      console.log("success:: ", data);

      // force condition as sometimes data does not include success flag
      // even when api is successful
      if (data.success === false) {
        toast(
          <ToastMessage title="Request failed!" content={data.message ?? ""} />,
          { type: "error", autoClose: false }
        );
      }

      if (Array.isArray(data.results)) {
        data.results.forEach((result) => {
          if (result.success) {
            // Format amount using Decimal.js for proper decimal display
            let formattedAmount = result.amount || "0";
            try {
              if (result.amount) {
                const decimalAmount = new Decimal(result.amount);
                // Format with commas and up to 8 decimal places, trimming trailing zeros
                formattedAmount = decimalAmount.toDP(8).toString();
                // Convert scientific notation if needed
                if (formattedAmount.includes("e")) {
                  formattedAmount = decimalAmount.toFixed();
                }
                // Add commas for thousands
                formattedAmount = new Decimal(formattedAmount)
                  .toNumber()
                  .toLocaleString("en-US", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 8,
                  });
              }
            } catch (e) {
              console.error("Error formatting decimal:", e);
              // Fall back to original amount if there's an error
              formattedAmount = result.amount || "0";
            }

            toast(
              <ToastMessage
                title="Success"
                content={
                  <div>
                    <p>
                      Received{" "}
                      <span className="font-medium">{formattedAmount}</span>{" "}
                      {result.tokenSymbol}
                    </p>
                    {result.tx && (
                      <a
                        href={result.tx}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 underline truncate"
                      >
                        View Transaction
                      </a>
                    )}
                  </div>
                }
              />,
              { type: "success", autoClose: 10000 }
            );
          } else {
            toast(
              <ToastMessage
                title="Request failed!"
                content={result.message ?? ""}
              />,
              { type: "error" }
            );
          }
        });
      }
    },
    onError(error) {
      console.log("error:: ", error);
      toast(
        <ToastMessage title="Request failed!" content={error.message ?? ""} />,
        { type: "error", autoClose: false }
      );
    },
  });
}
